import { type Breakpoints } from '@verbit-ai/verbit-ui-library'

import { Language, LanguageWithSpecialFont } from 'src/models/Language'

type ThemeZIndices = {
    appHeader: number
    trancriptToolbar: number
}

type ThemeSizes = {
    trancriptToolbarHeight: number
    trancriptToolbarHeightMobile: number
}

export type ThemeCore = {
    breakPoints: Breakpoints
    zIndexes: ThemeZIndices
    dimensions: ThemeSizes
}

type TextColorDescriptor = {
    colorValue: string
    colorTitle: string
}

export const TEXT_COLORS_AMOUNT = 6
export const DEFAULT_TEXT_COLOR_INDEX = 0

export const TEXT_BACKGROUND_COLORS_AMOUNT = 6
export const DEFAULT_TEXT_BACKGROUND_COLOR_INDEX = 0

export const APP_BACKGROUND_COLORS_AMOUNT = 2
export const DEFAULT_APP_BACKGROUND_COLOR_INDEX = 0

export const HEADER_HEIGHT_DEFAULT = 60
export const HEADER_HEIGHT_EXTENDED = 100

type PaletteGlobal = {
    focusIndicatorColor: string
    iconColor: string
}

type PaletteHeader = {
    logo: React.JSX.Element
    titleColor: string
    panelBackgroundColor: string
    panelFilter: string
    panelBorder: string
    linkColor: string
    dropdownTextColor: string
    dropdownIconColor: string
    dropdownBgColor: string
    dropdownBorderColor: string
}

type PaletteTranscript = {
    toolbarOuterBackground: string
    toolbarInnerBackground: string
    // speaker, timestamp and app background colors actually only change in green chromakey mode
    speakerColors: Tuple<string, typeof TEXT_COLORS_AMOUNT>
    timestampColors: Tuple<string, typeof TEXT_COLORS_AMOUNT>
    appBackgroundColors: Tuple<TextColorDescriptor, typeof APP_BACKGROUND_COLORS_AMOUNT>
    textColors: Tuple<TextColorDescriptor, typeof TEXT_COLORS_AMOUNT>
    textBackgroundColors: Tuple<TextColorDescriptor, typeof TEXT_BACKGROUND_COLORS_AMOUNT>
    loaderColor: string
    searchResultHighlight: string
    activeParagraphOutline: string
}

type PaletteToolbar = {
    btnColor: string
    btnHoverColor: string
    mobilePanelBorder: string
    mobilePanelIconColor: string
    extrasIconColor: string
    extrasTextColor: string
    extrasBackground: string
    sessionDetailsColor: string
}

type PaletteVerbitButton = {
    primaryBackgroundColor: string
    primaryHoverBgColor: string
    primaryBorderColor: string
    primaryHoverBorderColor: string
    primaryTextColor: string
    secondaryBackgroundColor: string
    secondaryHoverBgColor: string
    secondaryBorderColor: string
    secondaryHoverBorderColor: string
    secondaryTextColor: string
}

type VerbitModal = {
    backgroundColor: string
    titleColor: string
    subtitleColor: string
    textColor: string
    closeIconColor: string
    closeHoverBgColor: string
}

type VerbitCheckbox = {
    labelColor: string
    boxBorderColor: string
    checkedBoxBgColor: string
    checkedBoxIconColor: string
}

type VerbitSelect = {
    borderColor: string
    valueColor: string
    indicatorColor: string
    focusedShadow: string
    listBgColor: string
    listShadow: string
    optionColor: string
    checkIconColor: string
    optionFocusedBgColor: string
}

type PaletteSearch = {
    outerBorderColor: string
    innerBorderColor: string
    inputBgColor: string
    textColor: string
    placeholderColor: string
    clearButtonColor: string
    navButtonColor: string
    resultsColor: string
}

type PaletteFontSizeControl = {
    backgroundColor: string
    iconColor: string
    textColor: string
    optionHoverColor: string
    delimiterColor: string
}

type PaletteDownloadControl = {
    backgroundColor: string
    textColor: string
    optionHoverColor: string
}

type PaletteThemeToggle = {
    backgroundColor: string
    iconColor: string
    textColor: string
    titleColor: string
    colorOptionsBgColor: string
    fontOptionsBorderColor: string
    defaultColorIndicatorStroke: string
    colorIndicatorActiveColor: string
    mobileColorIndicatorBorderColor: string
    mobileButtonBorderColor: string
    mobileActiveColor: string
}

type PaletteLanguageSelect = {
    desktopTextColor: string
    desktopBgColor: string
    mobileTextColor: string
    iconColor: string
    separatorColor: string
    mobileTitleColor: string
    mobileBgColor: string
}

type PaletteToast = {
    backgroundColor: string
    borderColor: string
    textColor: string
    successTextColor: string
    actionColor: string
    successIconColor: string
    infoIconColor: string
    closeIconColor: string
    shadow: string
}

type PaletteScrollDownButtonChrome = {
    iconColor: string
    backgroundColor: string
}

type PaletteScrollDownButton = {
    borderColor: string
    shadow: string
    attentionPulseColor: string
    autoscrollOff: PaletteScrollDownButtonChrome
    autoscrollOn: PaletteScrollDownButtonChrome
}

type PaletteInconsistencyIndicator = {
    color: string
    hoverBackgroundColor: string
    // The icon or number on the selected (filled) indicator, and the count badge.
    selectedContentColor: string
    // A paragraph with inconsistencies, while hovered.
    paragraphHoverBackgroundColor: string
}

type PaletteRoutingError = {
    backgroundColor: string
    textColor: string
    linkColor: string
}

type PaletteEditor = {
    activeMediaTimingHighlight: string
}

type PaletteVerbitSwitch = {
    default: string
    trackActiveBgColor: string
    trackInactiveBgColor: string
}

type PaletteQuickActionMenu = {
    backgroundColor: string
    dividerColor: string
    buttonColor: string
    buttonHoverBgColor: string
    buttonDisabledColor: string
}

export type Palette = {
    global: PaletteGlobal
    header: PaletteHeader
    transcript: PaletteTranscript
    toolbar: PaletteToolbar
    verbitButton: PaletteVerbitButton
    verbitModal: VerbitModal
    verbitCheckbox: VerbitCheckbox
    verbitSelect: VerbitSelect
    verbitSwitch: PaletteVerbitSwitch
    search: PaletteSearch
    fontSizeControl: PaletteFontSizeControl
    downloadControl: PaletteDownloadControl
    themeToggle: PaletteThemeToggle
    languageSelect: PaletteLanguageSelect
    toast: PaletteToast
    scrollDownButton: PaletteScrollDownButton
    inconsistencyIndicator: PaletteInconsistencyIndicator
    routingError: PaletteRoutingError
    editor: PaletteEditor
    quickActionMenu: PaletteQuickActionMenu
}

// note: these values should match with `font-family` names provided
// in `@font-face` rules in `index.css`
export const FONT_FAMILIES = ['Arial', 'Inter', 'Roboto', 'SourceSerif', 'NotoSansSC', 'NotoSansJP', 'NotoSansKR', 'Hind'] as const

export type FontFamily = (typeof FONT_FAMILIES)[number]

const FONT_FAMILIES_FOR_SPECIAL_FONTS: Record<LanguageWithSpecialFont, FontFamily> = {
    [Language.Chinese]: 'NotoSansSC',
    [Language.Japanese]: 'NotoSansJP',
    [Language.Korean]: 'NotoSansKR',
    [Language.Hindi]: 'Hind',
}
export const DEFAULT_FONT_FAMILY_BY_LANGUAGE: Record<Language, FontFamily> = {
    // for the majority of languages, default font is Inter
    ...Object.values(Language).reduce((acc, curr) => Object.assign(acc, { [curr]: 'Inter' }), {} as Record<Language, FontFamily>),
    // "exotic" languages, not supported by Inter, have their own dedicated fonts
    ...FONT_FAMILIES_FOR_SPECIAL_FONTS,
}

export type Theme = {
    palette: Palette
    fontFamily: FontFamily
    availableFontFamilies: FontFamily[]
    textColorIndex: number
    textBackgroundColorIndex: number
    appBackgroundColorIndex: number
    headerHeight: number
    themeType: ThemeType
} & ThemeCore

export const THEME_TYPES = ['default', 'dark', 'highContrast', 'chromakey'] as const

export type ThemeType = (typeof THEME_TYPES)[number]

export const DEFAULT_THEME_TYPE: ThemeType = 'default'

export type ConfigurableThemeFields = Pick<
    Theme,
    'themeType' | 'fontFamily' | 'textColorIndex' | 'textBackgroundColorIndex' | 'appBackgroundColorIndex' | 'headerHeight'
>
